<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import { formatBytes } from 'shared/helpers/FileHelper';

const props = defineProps({
  // Pending attachment as stored by ReplyBox#attachFile:
  //   { resource, thumb, ... }
  // `resource` is EITHER an ActiveStorage DirectUpload blob descriptor
  //   ({ filename, content_type, byte_size, signed_id }) on the direct-upload path,
  // OR a file wrapper ({ name, type, size, file }) on the indirect path —
  // so metadata is read via dual fields (filename||name, content_type||type, byte_size||size).
  // `thumb` is ALWAYS a base64 data URL of the file (readAsDataURL), correctly typed
  // (data:application/pdf;base64,…, data:image/png;base64,…), available on both paths.
  // We preview from `thumb` because the raw File/Blob is not retained on the direct path.
  attachment: {
    type: Object,
    default: null,
  },
});

const { t } = useI18n();

const dialogRef = ref(null);

const resource = computed(() => props.attachment?.resource ?? null);

const source = computed(() => props.attachment?.thumb ?? '');

const fileName = computed(
  () => resource.value?.filename || resource.value?.name || ''
);

const fileSize = computed(() => {
  const size = resource.value?.byte_size ?? resource.value?.size;
  return size ? formatBytes(size, 0) : '';
});

const mime = computed(
  () => resource.value?.content_type || resource.value?.type || ''
);

const extension = computed(() => {
  const dot = fileName.value.lastIndexOf('.');
  return dot >= 0 ? fileName.value.slice(dot + 1).toLowerCase() : '';
});

// Resolve the preview kind from MIME first, falling back to the extension —
// some uploads report an empty content type.
const kind = computed(() => {
  const type = mime.value;
  if (type.startsWith('image/')) return 'image';
  if (type === 'application/pdf') return 'pdf';
  if (type.startsWith('video/')) return 'video';
  if (type.startsWith('audio/')) return 'audio';

  const ext = extension.value;
  if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'svg'].includes(ext))
    return 'image';
  if (ext === 'pdf') return 'pdf';
  if (['mp4', 'webm', 'ogg', 'mov', 'm4v'].includes(ext)) return 'video';
  if (['mp3', 'wav', 'oga', 'm4a', 'aac'].includes(ext)) return 'audio';
  return 'other';
});

const open = () => {
  dialogRef.value?.open();
};

defineExpose({ open });
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="fileName"
    width="3xl"
    overflow-y-auto
    :show-confirm-button="false"
    :show-cancel-button="false"
  >
    <div
      class="flex items-center justify-center w-full min-h-[12rem] max-h-[70vh] overflow-auto rounded-lg bg-n-alpha-2"
    >
      <img
        v-if="kind === 'image' && source"
        :src="source"
        :alt="fileName"
        class="object-contain max-w-full max-h-[70vh]"
      />
      <!-- source é uma data: URL (origem opaca) → não acessa a origem do app; sem necessidade
           de sandbox, que ainda quebraria o visualizador nativo de PDF. -->
      <iframe
        v-else-if="kind === 'pdf' && source"
        :src="source"
        :title="fileName"
        class="w-full h-[70vh] border-0 rounded-lg"
      />
      <video
        v-else-if="kind === 'video' && source"
        :src="source"
        controls
        class="max-w-full max-h-[70vh] rounded-lg"
      />
      <audio
        v-else-if="kind === 'audio' && source"
        :src="source"
        controls
        class="w-full px-6"
      />
      <div
        v-else
        class="flex flex-col items-center justify-center gap-3 py-10 text-center"
      >
        <span class="text-5xl">📄</span>
        <span class="text-sm font-medium text-n-slate-12 break-all px-6">
          {{ fileName }}
        </span>
        <span v-if="fileSize" class="text-xs text-n-slate-11">
          {{ fileSize }}
        </span>
        <span class="text-sm text-n-slate-11 max-w-sm">
          {{ t('CONVERSATION.REPLYBOX.ATTACHMENT_PREVIEW.NO_PREVIEW') }}
        </span>
      </div>
    </div>

    <template #footer>
      <div class="flex items-center justify-end w-full">
        <Button
          color="slate"
          :label="t('CONVERSATION.REPLYBOX.ATTACHMENT_PREVIEW.CLOSE')"
          @click="dialogRef?.close()"
        />
      </div>
    </template>
  </Dialog>
</template>
